import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

export default function ContactPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    states: [] as string[],
    interest: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const states = [
    "New Jersey",
    "Connecticut",
    "Massachusetts",
    "New York",
    "Pennsylvania",
    "Maryland",
    "Rhode Island",
    "Delaware",
  ];

  const interests = [
    "Request a demo call",
    "Get pricing information",
    "Partner with SolarLeadSource",
    "General inquiry",
  ];

  const toggleState = (state: string) => {
    setFormData((prev) => ({
      ...prev,
      states: prev.states.includes(state)
        ? prev.states.filter((s) => s !== state)
        : [...prev.states, state],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    if (!formData.name || !formData.email || !formData.interest) {
      setError("Please fill in all required fields.");
      setSubmitting(false);
      return;
    }

    const formPayload = new URLSearchParams();
    formPayload.append("name", formData.name);
    formPayload.append("email", formData.email);
    formPayload.append("company", formData.company);
    formPayload.append("phone", formData.phone);
    formPayload.append("states", formData.states.join(", "));
    formPayload.append("interest", formData.interest);
    formPayload.append("message", formData.message);

    try {
      const response = await fetch("https://readdy.ai/api/form/d79j9ob2hsar0k5m0o30", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formPayload.toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: "",
          email: "",
          company: "",
          phone: "",
          states: [],
          interest: "",
          message: "",
        });
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch {
      setError("Failed to submit. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar onRequestLeads={() => navigate("/signup")} />

      {/* Hero */}
      <div className="pt-24 pb-12 bg-gradient-to-br from-[#f0fdf4] to-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#16a34a]/10 text-[#16a34a] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <i className="ri-headphone-line"></i>
              Get in Touch
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ready to grow your solar business?
            </h1>
            <p className="text-gray-600 text-lg">
              Request a demo call or reach out with questions. Our team responds within 24 hours.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Form + Info */}
      <div className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="bg-[#f0fdf4] rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 bg-[#16a34a] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-white text-2xl"></i>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Message sent!</h2>
                  <p className="text-gray-600 mb-6">
                    Thanks for reaching out. We will get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="text-[#16a34a] font-medium hover:underline cursor-pointer"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8"
                >
                  {error && (
                    <div className="bg-red-50 text-red-600 text-sm p-4 rounded-xl mb-6">
                      {error}
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20 outline-none transition-all"
                        placeholder="John Smith"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20 outline-none transition-all"
                        placeholder="john@company.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20 outline-none transition-all"
                        placeholder="Your Solar Company"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20 outline-none transition-all"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Which states do you serve? (Select all that apply)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {states.map((state) => (
                        <button
                          key={state}
                          type="button"
                          onClick={() => toggleState(state)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                            formData.states.includes(state)
                              ? "bg-[#16a34a] text-white"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          {formData.states.includes(state) && (
                            <i className="ri-check-line mr-1"></i>
                          )}
                          {state}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      What are you interested in? <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      {interests.map((interest) => (
                        <label
                          key={interest}
                          className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:border-[#16a34a]/50 hover:bg-[#f0fdf4]/50 transition-all"
                        >
                          <input
                            type="radio"
                            name="interest"
                            value={interest}
                            checked={formData.interest === interest}
                            onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                            className="w-4 h-4 text-[#16a34a] focus:ring-[#16a34a]"
                            required
                          />
                          <span className="text-sm text-gray-700">{interest}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message (Optional)
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20 outline-none transition-all resize-none"
                      placeholder="Tell us about your business and what you are looking for..."
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">
                      {formData.message.length}/500
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#16a34a] text-white font-semibold py-4 rounded-xl hover:bg-[#15803d] transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </span>
                    ) : (
                      "Send Message"
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Why installers choose us</h3>
                <div className="space-y-4">
                  {[
                    { icon: "ri-shield-check-line", text: "100% exclusive leads — never shared" },
                    { icon: "ri-bar-chart-line", text: "Scored 50–99 for quality filtering" },
                    { icon: "ri-map-pin-line", text: "Northeast US coverage" },
                    { icon: "ri-time-line", text: "5-minute reservation window" },
                    { icon: "ri-award-line", text: "No contracts, no minimums" },
                  ].map((item) => (
                    <div key={item.text} className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-[#f0fdf4] rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-[#16a34a]`}></i>
                      </div>
                      <p className="text-sm text-gray-600">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Contact info</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-gray-400"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Email</p>
                      <a href="mailto:hello@solarleadsource.com" className="text-sm text-[#16a34a] hover:underline">
                        hello@solarleadsource.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-gray-400"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Response time</p>
                      <p className="text-sm text-gray-600">Within 24 hours</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#f0fdf4] rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-[#16a34a]">47</p>
                  <p className="text-xs text-gray-600">Active installers</p>
                </div>
                <div className="bg-[#f0fdf4] rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-[#16a34a]">4.9</p>
                  <p className="text-xs text-gray-600">Avg. rating</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="bg-[#16a34a] rounded-3xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to see the platform?
            </h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Schedule a 15-minute demo call. We will show you how the lead marketplace works and answer any questions.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate("/signup")}
                className="bg-white text-[#16a34a] font-semibold px-6 py-3 rounded-xl hover:bg-white/90 transition-all cursor-pointer whitespace-nowrap"
              >
                Create Free Account
              </button>
              <button
                onClick={() => navigate("/pricing")}
                className="bg-[#15803d] text-white font-semibold px-6 py-3 rounded-xl hover:bg-[#166534] transition-all cursor-pointer whitespace-nowrap"
              >
                View Pricing
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}